import React, { useRef, useEffect } from 'react';

const ORANGE = '#f97316';
const CYAN = '#22d3ee';
const EMERALD = '#00FF94';
const AMBER = '#f59e0b';
const INDIGO = '#6366f1';
const ROSE = '#f43f5e';
const nodeColors = [ORANGE, CYAN, EMERALD, AMBER, INDIGO, ROSE];

const LABEL_NAMES = ['KV','D1','R2','DO','CF','W1','W2','PG','S3','API','CDN','DNS','WAF','SSL','TCP','WSS','GPU','CPU','MEM','SSD','EU','US','AP','SA'];

const NODE_COUNT = 90;

function rotatePoint(x, y, z, angleY, angleX) {
  const x1 = x * Math.cos(angleY) + z * Math.sin(angleY);
  const z1 = -x * Math.sin(angleY) + z * Math.cos(angleY);
  const y2 = y * Math.cos(angleX) - z1 * Math.sin(angleX);
  const z2 = y * Math.sin(angleX) + z1 * Math.cos(angleX);
  return { x: x1, y: y2, z: z2 };
}

function project(x, y, z, radius, cx, cy) {
  const perspective = 3;
  const scale = perspective / (perspective + z);
  return { x: cx + x * radius * scale, y: cy + y * radius * scale, scale, z };
}

const InfraHeroBg = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, animId, frame = 0;

    // Build sphere nodes
    const sphereNodes = [];
    let labelIdx = 0;
    for (let i = 0; i < NODE_COUNT; i++) {
      const phi = Math.acos(1 - 2 * (i + 0.5) / NODE_COUNT);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      const isMain = Math.random() > 0.55;
      sphereNodes.push({
        bx: Math.sin(phi) * Math.cos(theta),
        by: Math.sin(phi) * Math.sin(theta),
        bz: Math.cos(phi),
        color: nodeColors[Math.floor(Math.random() * nodeColors.length)],
        size: 2 + Math.random() * 3,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.02 + Math.random() * 0.02,
        isMain,
        label: isMain ? LABEL_NAMES[labelIdx++ % LABEL_NAMES.length] : '',
        labelAlpha: 0,
        labelTarget: 0,
        labelTimer: 0,
      });
    }

    // Edges
    const edges = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      const dists = [];
      for (let j = i + 1; j < NODE_COUNT; j++) {
        const dx = sphereNodes[i].bx - sphereNodes[j].bx;
        const dy = sphereNodes[i].by - sphereNodes[j].by;
        const dz = sphereNodes[i].bz - sphereNodes[j].bz;
        dists.push({ j, d: Math.sqrt(dx * dx + dy * dy + dz * dz) });
      }
      dists.sort((a, b) => a.d - b.d);
      const count = 3 + Math.floor(Math.random() * 2);
      for (let k = 0; k < count && k < dists.length; k++) {
        if (dists[k].d < 0.85) edges.push({ a: i, b: dists[k].j });
      }
    }

    // Pulses
    const pulses = [];
    function spawnPulse() {
      if (!edges.length) return;
      const edge = edges[Math.floor(Math.random() * edges.length)];
      const forward = Math.random() > 0.5;
      pulses.push({
        from: forward ? edge.a : edge.b,
        to: forward ? edge.b : edge.a,
        progress: 0,
        speed: 0.008 + Math.random() * 0.008,
        color: sphereNodes[forward ? edge.b : edge.a].color,
        size: 1.5 + Math.random() * 1.5,
      });
    }

    // Travelers
    const travelers = [];
    function spawnTraveler() {
      const mainIndices = sphereNodes.map((n, i) => n.isMain ? i : -1).filter(i => i >= 0);
      if (mainIndices.length < 2) return;
      const path = [];
      let current = mainIndices[Math.floor(Math.random() * mainIndices.length)];
      path.push(current);
      const hops = 2 + Math.floor(Math.random() * 3);
      for (let h = 0; h < hops; h++) {
        let best = -1, bestD = Infinity;
        for (const mi of mainIndices) {
          if (path.includes(mi)) continue;
          const dx = sphereNodes[current].bx - sphereNodes[mi].bx;
          const dy = sphereNodes[current].by - sphereNodes[mi].by;
          const dz = sphereNodes[current].bz - sphereNodes[mi].bz;
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d < bestD) { bestD = d; best = mi; }
        }
        if (best >= 0) { path.push(best); current = best; }
      }
      if (path.length < 2) return;
      const colors = [ORANGE, CYAN, EMERALD, AMBER];
      travelers.push({
        path, hop: 0, progress: 0,
        speed: 0.004 + Math.random() * 0.003,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 2,
        trail: [],
      });
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      if (!parent) return;
      W = parent.clientWidth;
      H = parent.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function render() {
      frame++;
      ctx.clearRect(0, 0, W, H);

      const cx = W * 0.5;
      const cy = H * 0.5;
      const radius = Math.max(W, H) * 0.55;
      const rotY = frame * 0.003;
      const rotX = 0.3;

      // Project nodes
      const projected = [];
      for (const node of sphereNodes) {
        const rot = rotatePoint(node.bx, node.by, node.bz, rotY, rotX);
        const p = project(rot.x, rot.y, rot.z, radius, cx, cy);
        projected.push({ ...p, node, rot });
      }

      const sortedIndices = projected.map((_, i) => i).sort((a, b) => projected[a].z - projected[b].z);

      // Edges
      for (const edge of edges) {
        const pa = projected[edge.a];
        const pb = projected[edge.b];
        const avgZ = (pa.z + pb.z) / 2;
        const depthFade = 0.3 + 0.7 * ((avgZ + 1) / 2);
        ctx.strokeStyle = ORANGE;
        ctx.globalAlpha = 0.02 + depthFade * 0.04;
        ctx.lineWidth = 0.5 + depthFade * 0.5;
        ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke();
      }

      // Pulses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.progress += p.speed;
        if (p.progress >= 1) { pulses.splice(i, 1); continue; }
        const pa = projected[p.from];
        const pb = projected[p.to];
        const px = pa.x + (pb.x - pa.x) * p.progress;
        const py = pa.y + (pb.y - pa.y) * p.progress;
        const pz = pa.z + (pb.z - pa.z) * p.progress;
        const depthFade = 0.3 + 0.7 * ((pz + 1) / 2);

        for (let t = 0; t < 6; t++) {
          const tp = p.progress - t * 0.015;
          if (tp < 0) continue;
          const tx = pa.x + (pb.x - pa.x) * tp;
          const ty = pa.y + (pb.y - pa.y) * tp;
          ctx.fillStyle = p.color;
          ctx.globalAlpha = (1 - t / 6) * 0.2 * depthFade;
          ctx.beginPath(); ctx.arc(tx, ty, p.size * (1 - t / 6 * 0.5), 0, Math.PI * 2); ctx.fill();
        }

        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.5 * depthFade;
        ctx.shadowColor = p.color; ctx.shadowBlur = 6;
        ctx.beginPath(); ctx.arc(px, py, p.size, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }

      // Nodes
      for (const idx of sortedIndices) {
        const p = projected[idx];
        const node = p.node;
        const depthFade = 0.2 + 0.8 * ((p.z + 1) / 2);
        const pulse = Math.sin(frame * node.pulseSpeed + node.pulsePhase) * 0.3 + 0.7;
        const baseSize = node.isMain ? node.size * 1.3 : node.size;
        const sz = baseSize * p.scale * (0.8 + pulse * 0.2);

        if (node.isMain && depthFade > 0.4) {
          ctx.strokeStyle = node.color;
          ctx.globalAlpha = 0.05 * depthFade * pulse;
          ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(p.x, p.y, sz + 6, 0, Math.PI * 2); ctx.stroke();
          const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, sz + 10);
          grd.addColorStop(0, node.color); grd.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = grd;
          ctx.globalAlpha = 0.04 * depthFade;
          ctx.beginPath(); ctx.arc(p.x, p.y, sz + 10, 0, Math.PI * 2); ctx.fill();
        }

        ctx.fillStyle = node.color;
        ctx.globalAlpha = (0.15 + depthFade * 0.5) * pulse;
        ctx.beginPath(); ctx.arc(p.x, p.y, sz, 0, Math.PI * 2); ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = (0.1 + depthFade * 0.4) * pulse;
        ctx.beginPath(); ctx.arc(p.x, p.y, sz * 0.35, 0, Math.PI * 2); ctx.fill();

        if (node.isMain && node.label) {
          node.labelTimer--;
          if (node.labelTimer <= 0) {
            node.labelTarget = node.labelTarget > 0.5 ? 0 : 1;
            node.labelTimer = 120 + Math.floor(Math.random() * 200);
          }
          node.labelAlpha += (node.labelTarget - node.labelAlpha) * 0.03;
          if (node.labelAlpha > 0.02 && depthFade > 0.35) {
            const labelAlpha = node.labelAlpha * depthFade * 0.6;
            const fontSize = Math.round(9 * p.scale);
            ctx.font = `bold ${fontSize}px 'SF Mono','Fira Code',monospace`;
            ctx.fillStyle = node.color;
            ctx.globalAlpha = labelAlpha;
            ctx.textAlign = 'center';
            ctx.fillText(node.label, p.x, p.y - sz - 6);
          }
        }
      }

      // Travelers
      for (let i = travelers.length - 1; i >= 0; i--) {
        const t = travelers[i];
        t.progress += t.speed;
        if (t.progress >= 1) {
          const arriveNode = sphereNodes[t.path[t.hop + 1]];
          if (arriveNode) { arriveNode.labelTarget = 1; arriveNode.labelTimer = 80 + Math.floor(Math.random() * 60); }
          t.hop++; t.progress = 0;
          if (t.hop >= t.path.length - 1) { travelers.splice(i, 1); continue; }
        }
        const pa = projected[t.path[t.hop]];
        const pb = projected[t.path[t.hop + 1]];
        const px = pa.x + (pb.x - pa.x) * t.progress;
        const py = pa.y + (pb.y - pa.y) * t.progress;
        const pz = pa.z + (pb.z - pa.z) * t.progress;
        const depthFade = 0.3 + 0.7 * ((pz + 1) / 2);

        t.trail.push({ x: px, y: py, df: depthFade });
        if (t.trail.length > 20) t.trail.shift();

        for (let tr = 0; tr < t.trail.length; tr++) {
          const tp = t.trail[tr];
          ctx.fillStyle = t.color;
          ctx.globalAlpha = (tr / t.trail.length) * 0.15 * tp.df;
          ctx.beginPath(); ctx.arc(tp.x, tp.y, t.size * (0.3 + 0.7 * tr / t.trail.length), 0, Math.PI * 2); ctx.fill();
        }

        ctx.fillStyle = t.color;
        ctx.globalAlpha = 0.6 * depthFade;
        ctx.shadowColor = t.color; ctx.shadowBlur = 12;
        ctx.beginPath(); ctx.arc(px, py, t.size, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.globalAlpha = 0.5 * depthFade;
        ctx.beginPath(); ctx.arc(px, py, t.size * 0.35, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }

      ctx.globalAlpha = 1;

      // Top & bottom fade
      const fadeH = H * 0.22;
      const topFade = ctx.createLinearGradient(0, 0, 0, fadeH);
      topFade.addColorStop(0, '#0A0E17'); topFade.addColorStop(1, 'rgba(10, 14, 23, 0)');
      ctx.fillStyle = topFade; ctx.fillRect(0, 0, W, fadeH);

      const botFade = ctx.createLinearGradient(0, H - fadeH, 0, H);
      botFade.addColorStop(0, 'rgba(10, 14, 23, 0)'); botFade.addColorStop(1, '#0A0E17');
      ctx.fillStyle = botFade; ctx.fillRect(0, H - fadeH, W, fadeH);

      // Vignette
      const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.95);
      vig.addColorStop(0, 'rgba(0,0,0,0)'); vig.addColorStop(1, 'rgba(0,0,0,0.4)');
      ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);

      if (frame % 10 === 0) spawnPulse();
      if (frame % 80 === 0) spawnTraveler();

      animId = requestAnimationFrame(render);
    }

    resize();
    render();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

export default InfraHeroBg;
